import {
  Box,
  Card,
  CardBody,
  Flex,
  Link,
  Progress,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ExternalIngestionFolderListRowFragment } from '@client/graphql/__generated__/types';
import { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useShowBlurOnScrollable } from '~/apps/consumer/hooks/useShowBlurOnScrollable';
import { formatKeyTermValue } from '~/common/pages/Compliance/utils/ingestionDocuments.utils';
import { formatUnderscoreField } from '~/common/utils/formatter';
import { extractNamesFromMetadata } from '../../../../common/pages/Compliance/utils/ingestionFolder';

interface IngestionFoldersTableProps {
  folders: ExternalIngestionFolderListRowFragment[];
  loading: boolean;
  fetchTriggerRef: (ref: HTMLDivElement | null) => void;
  setInitialBlurRef?: (ref: HTMLDivElement | null) => void;
  handleBlurOnScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
}

export const IngestionFoldersTable: FC<IngestionFoldersTableProps> = ({
  folders,
  loading,
  fetchTriggerRef,
  setInitialBlurRef,
  handleBlurOnScroll,
}) => {
  return (
    <Flex direction="column" h="100%" minH="0">
      <Progress
        isIndeterminate
        height="2px"
        size="xs"
        visibility={loading ? 'visible' : 'hidden'}
        width="100%"
      />
      <Flex
        ref={setInitialBlurRef}
        direction="column"
        flex="1"
        minH="0"
        overflow="auto"
        position="relative"
        onScroll={handleBlurOnScroll}
      >
        <Table size="md" variant="simple">
          <Thead
            bgColor="opaque.indigo_800_whiteAlpha_100"
            position="sticky"
            top={0}
            zIndex={1}
          >
            <Tr>
              <Th>Folder Name</Th>
              <Th>Side</Th>
              <Th>Agent</Th>
              <Th>Client</Th>
              <Th>State</Th>
              <Th>Status</Th>
              <Th>Agency Comp Text</Th>
              <Th>Agency Comp Flat</Th>
              <Th>Agency Comp Percent</Th>
              <Th>Agency Exp Date</Th>
              <Th>Purchase Price</Th>
              <Th>Close Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {folders.map((folder: ExternalIngestionFolderListRowFragment) => {
              const { agentName, clientName } = extractNamesFromMetadata(
                folder.metadata,
                folder.transactionSide
              );

              const keyTermColumns = folder.keyTermColumns || {};
              const purchasePrice = formatKeyTermValue(
                keyTermColumns.purchase_price,
                'purchase_price'
              );
              const closeDate = formatKeyTermValue(
                keyTermColumns.close_date,
                'close_date'
              );
              const compensationText =
                keyTermColumns.agency_broker_compensation_text || '-';
              const compensationFlat = formatKeyTermValue(
                keyTermColumns.agency_broker_compensation_flat,
                'agency_broker_compensation_flat'
              );
              const compensationPercent = formatKeyTermValue(
                keyTermColumns.agency_broker_compensation_pct,
                'agency_broker_compensation_pct'
              );
              const expirationDate = formatKeyTermValue(
                keyTermColumns.expiration_date,
                'expiration_date'
              );

              return (
                <Link
                  key={folder.id}
                  as={RouterLink}
                  cursor="pointer"
                  display="contents"
                  to={`/organizations/compliance/${folder.id}`}
                >
                  <Tr _hover={{ bg: 'whiteAlpha.100' }} transition="all 0.2s">
                    <Td fontWeight="medium">{folder.folderName}</Td>
                    <Td>{folder.transactionSide}</Td>
                    <Td>{agentName}</Td>
                    <Td>{clientName}</Td>
                    <Td>
                      {folder.transactionPipeline?.usState
                        ? folder.transactionPipeline.usState
                        : '-'}
                    </Td>
                    <Td>{formatUnderscoreField(folder.status)}</Td>
                    <Td>{compensationText}</Td>
                    <Td>{compensationFlat}</Td>
                    <Td>{compensationPercent}</Td>
                    <Td>{expirationDate}</Td>
                    <Td>{purchasePrice}</Td>
                    <Td>{closeDate}</Td>
                  </Tr>
                </Link>
              );
            })}
            <Tr>
              <Td border="none" colSpan={12} p={0}>
                <Box ref={fetchTriggerRef} height="1px" width="100%" />
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Flex>
    </Flex>
  );
};

interface IngestionFoldersTableCardProps {
  folders: ExternalIngestionFolderListRowFragment[];
  loading: boolean;
  fetchTriggerRef: (ref: HTMLDivElement | null) => void;
  hasActiveFilters: boolean;
}

export const IngestionFoldersTableCard: FC<IngestionFoldersTableCardProps> = ({
  folders,
  loading,
  fetchTriggerRef,
  hasActiveFilters,
}) => {
  const { showBlur, setInitialBlurRef, handleBlurOnScroll } =
    useShowBlurOnScrollable();

  return (
    <Card
      _after={{
        display: showBlur ? 'block' : 'none',
        content: '""',
        background:
          'linear-gradient(0deg, #111527 0%, rgba(17, 21, 39, 0.00) 100%)',
        width: '100%',
        height: '50px',
        position: 'absolute',
        bottom: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 2,
      }}
      display="flex"
      flex="1"
      flexDirection="column"
      minH="0"
      overflow="hidden"
      position="relative"
    >
      <CardBody display="flex" flex="1" flexDirection="column" minH="0" p={0}>
        {folders.length === 0 && !loading ? (
          <Box p={6}>
            <Text color="gray.500">
              {hasActiveFilters
                ? 'No ingestion folders match the current filters. Try adjusting your search or filters.'
                : 'No ingestion folders found for this organization.'}
            </Text>
          </Box>
        ) : (
          <IngestionFoldersTable
            fetchTriggerRef={fetchTriggerRef}
            folders={folders}
            handleBlurOnScroll={handleBlurOnScroll}
            loading={loading}
            setInitialBlurRef={setInitialBlurRef}
          />
        )}
      </CardBody>
    </Card>
  );
};
